// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Rafael Pereira Satyro

import { useState, useEffect, useCallback } from 'react';
import {
  Modal,
  Button,
  Icon,
  Table,
  Label,
  Header,
  Message,
  Segment,
  Input,
  Loader,
  Checkbox,
} from 'semantic-ui-react';
import { brokerageApi, Brokerage } from '../api/brokerageApi';
import { policyTypeApi, PolicyType } from '../api/policyTypeApi';
import { brokeragePolicyTypeApi, BrokeragePolicyType } from '../api/brokeragePolicyTypeApi';

interface PolicyTypeBrokerageModalProps {
  open: boolean;
  onClose: () => void;
  providerId: string;
  providerName: string;
  highlightedPolicyTypeId?: string;
}

interface BrokerageSection {
  brokerage: Brokerage;
  configs: Map<string, BrokeragePolicyType>;
}

function PolicyTypeBrokerageModal({
  open,
  onClose,
  providerId,
  providerName,
  highlightedPolicyTypeId,
}: Readonly<PolicyTypeBrokerageModalProps>) {
  const [brokerageSections, setBrokerageSections] = useState<BrokerageSection[]>([]);
  const [policyTypes, setPolicyTypes] = useState<PolicyType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [togglingKey, setTogglingKey] = useState<string | null>(null);
  const [bulkToggling, setBulkToggling] = useState<string | null>(null);
  const [searchFilter, setSearchFilter] = useState('');
  // selectedItems: Map<brokerageId, Set<policyTypeId>>
  const [selectedItems, setSelectedItems] = useState<Map<string, Set<string>>>(new Map());

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [brokerageData, policyTypeData] = await Promise.all([
        brokerageApi.getAll({ size: 1000 }),
        policyTypeApi.getAll({ providerId, isActive: true, size: 1000 }),
      ]);

      const brokerages = brokerageData.content;
      const pts = policyTypeData.content;
      setPolicyTypes(pts);

      const configResults = await Promise.allSettled(
        brokerages.map(async (brokerage) => {
          const configs = await brokeragePolicyTypeApi.getByProvider(
            brokerage.id!,
            providerId,
            { size: 1000 }
          );
          const configMap = new Map<string, BrokeragePolicyType>();
          for (const config of configs.content) {
            configMap.set(config.policyTypeId, config);
          }
          return { brokerage, configs: configMap } as BrokerageSection;
        })
      );

      const sections: BrokerageSection[] = [];
      for (const result of configResults) {
        if (result.status === 'fulfilled') {
          sections.push(result.value);
        }
      }

      setBrokerageSections(sections);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [providerId]);

  useEffect(() => {
    if (open) {
      loadData();
      setSearchFilter('');
      setSelectedItems(new Map());
    }
  }, [open, loadData]);

  const handleToggle = async (brokerageId: string, policyTypeId: string, isEnabled: boolean) => {
    const key = `${brokerageId}:${policyTypeId}`;
    setTogglingKey(key);
    try {
      if (isEnabled) {
        await brokeragePolicyTypeApi.disable(brokerageId, policyTypeId);
      } else {
        await brokeragePolicyTypeApi.enable(brokerageId, policyTypeId);
      }

      setBrokerageSections(prev =>
        prev.map(section => {
          if (section.brokerage.id !== brokerageId) return section;
          const newConfigs = new Map(section.configs);
          const existing = newConfigs.get(policyTypeId);
          if (existing) {
            newConfigs.set(policyTypeId, { ...existing, isEnabled: !isEnabled });
          } else {
            newConfigs.set(policyTypeId, {
              brokerageId,
              policyTypeId,
              isEnabled: true,
            } as BrokeragePolicyType);
          }
          return { ...section, configs: newConfigs };
        })
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to toggle policy type');
    } finally {
      setTogglingKey(null);
    }
  };

  const handleBulkToggle = async (brokerageId: string, enable: boolean) => {
    const selected = selectedItems.get(brokerageId);
    if (!selected || selected.size === 0) return;

    setBulkToggling(brokerageId);
    setError(null);
    try {
      const section = brokerageSections.find(s => s.brokerage.id === brokerageId);
      if (!section) return;

      // Only toggle items that need changing
      const toToggle = Array.from(selected).filter(ptId => {
        const config = section.configs.get(ptId);
        const isEnabled = config?.isEnabled || false;
        return enable ? !isEnabled : isEnabled;
      });

      const results = await Promise.allSettled(
        toToggle.map(ptId =>
          enable
            ? brokeragePolicyTypeApi.enable(brokerageId, ptId)
            : brokeragePolicyTypeApi.disable(brokerageId, ptId)
        )
      );

      const failedCount = results.filter(r => r.status === 'rejected').length;

      // Update local state for successful toggles
      setBrokerageSections(prev =>
        prev.map(s => {
          if (s.brokerage.id !== brokerageId) return s;
          const newConfigs = new Map(s.configs);
          results.forEach((result, index) => {
            if (result.status === 'fulfilled') {
              const ptId = toToggle[index];
              const existing = newConfigs.get(ptId);
              if (existing) {
                newConfigs.set(ptId, { ...existing, isEnabled: enable });
              } else {
                newConfigs.set(ptId, { brokerageId, policyTypeId: ptId, isEnabled: enable } as BrokeragePolicyType);
              }
            }
          });
          return { ...s, configs: newConfigs };
        })
      );

      // Clear selection for this brokerage
      setSelectedItems(prev => {
        const next = new Map(prev);
        next.delete(brokerageId);
        return next;
      });

      if (failedCount > 0) {
        setError(`${failedCount} policy type(s) failed to ${enable ? 'enable' : 'disable'}`);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Bulk operation failed');
    } finally {
      setBulkToggling(null);
    }
  };

  // Selection helpers
  const getSelectedForBrokerage = (brokerageId: string): Set<string> => {
    return selectedItems.get(brokerageId) || new Set();
  };

  const toggleSelection = (brokerageId: string, policyTypeId: string) => {
    setSelectedItems(prev => {
      const next = new Map(prev);
      const current = new Set(next.get(brokerageId) || []);
      if (current.has(policyTypeId)) {
        current.delete(policyTypeId);
      } else {
        current.add(policyTypeId);
      }
      if (current.size === 0) {
        next.delete(brokerageId);
      } else {
        next.set(brokerageId, current);
      }
      return next;
    });
  };

  const toggleSelectAll = (brokerageId: string, visiblePolicyTypeIds: string[]) => {
    setSelectedItems(prev => {
      const next = new Map(prev);
      const current = next.get(brokerageId) || new Set();
      const allSelected = visiblePolicyTypeIds.every(id => current.has(id));
      if (allSelected) {
        next.delete(brokerageId);
      } else {
        next.set(brokerageId, new Set(visiblePolicyTypeIds));
      }
      return next;
    });
  };

  const getEnabledCount = (section: BrokerageSection): number => {
    let count = 0;
    for (const config of section.configs.values()) {
      if (config.isEnabled) count++;
    }
    return count;
  };

  // Filter policy types by search term
  const filteredPolicyTypes = policyTypes.filter(pt => {
    if (!searchFilter) return true;
    const search = searchFilter.toLowerCase();
    return (
      pt.name.toLowerCase().includes(search) ||
      pt.code.toLowerCase().includes(search) ||
      (pt.description && pt.description.toLowerCase().includes(search))
    );
  });

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'PENSION': return 'blue';
      case 'INSURANCE': return 'green';
      case 'INVESTMENT': return 'orange';
      case 'BANKING': return 'purple';
      default: return 'grey';
    }
  };

  return (
    <Modal open={open} onClose={onClose} size="large" closeIcon>
      <Modal.Header>
        <Icon name="file alternate" /> Manage Policy Types - {providerName}
      </Modal.Header>
      <Modal.Content scrolling>
        {error && (
          <Message negative onDismiss={() => setError(null)}>
            <Message.Header>Error</Message.Header>
            <p>{error}</p>
          </Message>
        )}

        {loading ? (
          <Loader active inline="centered" content="Loading configuration..." />
        ) : policyTypes.length === 0 ? (
          <Message info>
            <Message.Header>No Policy Types</Message.Header>
            <p>This provider has no active policy types configured.</p>
          </Message>
        ) : (
          <>
            <Input
              icon="search"
              placeholder="Search policy types..."
              value={searchFilter}
              onChange={(e) => setSearchFilter(e.target.value)}
              fluid
              style={{ marginBottom: '1rem' }}
            />

            {filteredPolicyTypes.length === 0 ? (
              <Message info>
                <p>No policy types match the search.</p>
              </Message>
            ) : (
              brokerageSections.map(section => {
                const enabledCount = getEnabledCount(section);
                const brokerageId = section.brokerage.id!;
                const selected = getSelectedForBrokerage(brokerageId);
                const visibleIds = filteredPolicyTypes.map(pt => pt.id!);
                const allSelected = visibleIds.length > 0 && visibleIds.every(id => selected.has(id));
                const someSelected = selected.size > 0;
                const isBulkToggling = bulkToggling === brokerageId;

                return (
                  <Segment key={brokerageId}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                      <Header as="h4" style={{ margin: 0 }}>
                        <Icon name="building" />
                        <Header.Content>
                          {section.brokerage.name}
                          {section.brokerage.subdomain && (
                            <span style={{ color: '#999', marginLeft: '8px', fontWeight: 'normal' }}>
                              ({section.brokerage.subdomain})
                            </span>
                          )}
                          <Label
                            size="small"
                            color={enabledCount > 0 ? 'blue' : 'grey'}
                            style={{ marginLeft: '10px' }}
                          >
                            {enabledCount}/{policyTypes.length} enabled
                          </Label>
                        </Header.Content>
                      </Header>

                      {someSelected && (
                        <div style={{ display: 'flex', gap: '6px' }}>
                          <Button
                            size="mini"
                            color="green"
                            onClick={() => handleBulkToggle(brokerageId, true)}
                            loading={isBulkToggling}
                            disabled={isBulkToggling}
                          >
                            <Icon name="checkmark" /> Enable ({selected.size})
                          </Button>
                          <Button
                            size="mini"
                            color="red"
                            onClick={() => handleBulkToggle(brokerageId, false)}
                            loading={isBulkToggling}
                            disabled={isBulkToggling}
                          >
                            <Icon name="ban" /> Disable ({selected.size})
                          </Button>
                        </div>
                      )}
                    </div>

                    <Table compact celled size="small">
                      <Table.Header>
                        <Table.Row>
                          <Table.HeaderCell width={1} textAlign="center">
                            <Checkbox
                              checked={allSelected}
                              indeterminate={someSelected && !allSelected}
                              onChange={() => toggleSelectAll(brokerageId, visibleIds)}
                            />
                          </Table.HeaderCell>
                          <Table.HeaderCell>Policy Type</Table.HeaderCell>
                          <Table.HeaderCell width={2}>Category</Table.HeaderCell>
                          <Table.HeaderCell width={2}>Status</Table.HeaderCell>
                          <Table.HeaderCell width={2} textAlign="center">Action</Table.HeaderCell>
                        </Table.Row>
                      </Table.Header>
                      <Table.Body>
                        {filteredPolicyTypes.map(pt => {
                          const config = section.configs.get(pt.id!);
                          const isEnabled = config?.isEnabled || false;
                          const isHighlighted = pt.id === highlightedPolicyTypeId;
                          const toggleKey = `${brokerageId}:${pt.id}`;
                          const isToggling = togglingKey === toggleKey;
                          const isChecked = selected.has(pt.id!);

                          return (
                            <Table.Row
                              key={pt.id}
                              style={isHighlighted ? { backgroundColor: '#fffde7' } : undefined}
                            >
                              <Table.Cell textAlign="center">
                                <Checkbox
                                  checked={isChecked}
                                  onChange={() => toggleSelection(brokerageId, pt.id!)}
                                />
                              </Table.Cell>
                              <Table.Cell>
                                <strong>{pt.name}</strong>
                                {isHighlighted && (
                                  <Label size="mini" color="yellow" style={{ marginLeft: '8px' }}>
                                    Current
                                  </Label>
                                )}
                                {pt.description && (
                                  <div style={{ fontSize: '0.85em', color: '#666', marginTop: '2px' }}>
                                    {pt.description}
                                  </div>
                                )}
                              </Table.Cell>
                              <Table.Cell>
                                <Label size="small" color={getCategoryColor(pt.category)}>
                                  {pt.category}
                                </Label>
                              </Table.Cell>
                              <Table.Cell>
                                <Label size="small" color={isEnabled ? 'green' : 'grey'}>
                                  {isEnabled ? 'Enabled' : 'Disabled'}
                                </Label>
                              </Table.Cell>
                              <Table.Cell textAlign="center">
                                <Button
                                  size="mini"
                                  color={isEnabled ? 'red' : 'green'}
                                  onClick={() => handleToggle(brokerageId, pt.id!, isEnabled)}
                                  loading={isToggling}
                                  disabled={isToggling || isBulkToggling}
                                >
                                  <Icon name={isEnabled ? 'ban' : 'checkmark'} />
                                  {isEnabled ? 'Disable' : 'Enable'}
                                </Button>
                              </Table.Cell>
                            </Table.Row>
                          );
                        })}
                      </Table.Body>
                    </Table>
                  </Segment>
                );
              })
            )}
          </>
        )}
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={onClose}>
          <Icon name="checkmark" /> Done
        </Button>
      </Modal.Actions>
    </Modal>
  );
}

export default PolicyTypeBrokerageModal;
